import { useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useAuthStore } from '../stores/authStore'

interface AuthProviderProps {
  children: ReactNode
}

// Constants for token refresh timing
const ACCESS_TOKEN_LIFETIME = 15 * 60 * 1000 // 15 minutes in milliseconds
const REFRESH_BUFFER = 2 * 60 * 1000 // 2 minutes buffer before expiration
const REFRESH_INTERVAL = ACCESS_TOKEN_LIFETIME - REFRESH_BUFFER // Refresh at 13 minutes

export function AuthProvider({ children }: AuthProviderProps) {
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isInitializedRef = useRef(false)

  // Proactive token refresh function - useCallback to prevent recreation
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current)
      refreshTimerRef.current = null
    }

    // Only schedule refresh if user is authenticated
    const { isAuthenticated } = useAuthStore.getState()
    if (!isAuthenticated) {
      console.debug('[AuthProvider] User not authenticated, skipping refresh scheduling')
      return
    }

    console.debug(
      `[AuthProvider] Scheduling token refresh every ${REFRESH_INTERVAL / 1000 / 60} minutes (${REFRESH_BUFFER / 1000 / 60} min before expiration)`
    )

    // Refresh token proactively before expiration
    refreshTimerRef.current = setInterval(async () => {
      try {
        const { isAuthenticated: stillAuthenticated } = useAuthStore.getState()
        if (!stillAuthenticated) {
          console.debug('[AuthProvider] User no longer authenticated, stopping refresh')
          if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current)
            refreshTimerRef.current = null
          }
          return
        }

        console.debug('[AuthProvider] 🔄 Starting proactive token refresh')
        const { authService } = await import('../services/auth')
        await authService.refreshTokenWithDedup()
        // Success is logged by authService
      } catch (error) {
        // Auth service handles logout if needed, we just log here
        console.debug('[AuthProvider] Proactive token refresh completed (may have failed):', error)
      }
    }, REFRESH_INTERVAL)

    console.debug('[AuthProvider] ✅ Token refresh timer scheduled successfully')
  }, [])

  // Initialize auth on mount - only run once
  useEffect(() => {
    // Prevent double initialization in React strict mode
    if (isInitializedRef.current) {
      console.debug('[AuthProvider] Already initialized, skipping')
      return
    }

    isInitializedRef.current = true
    console.debug('[AuthProvider] 🚀 Initializing auth system')

    // Initialize auth and verify session with backend
    const initializeAuth = async () => {
      try {
        // First restore from localStorage
        const { initialize } = useAuthStore.getState()
        initialize()
        console.debug('[AuthProvider] Auth state restored from localStorage')

        // Then verify with backend if user exists
        const { user, isAuthenticated } = useAuthStore.getState()
        if (isAuthenticated && user) {
          console.debug('[AuthProvider] User found in storage, verifying session with backend')
          try {
            // Verify session is still valid by making a simple API call
            // ApiService will automatically handle 401 and trigger refresh if needed
            const { apiService } = await import('../services/api')
            await apiService.get('/api/v1/auth/me')
            console.debug('[AuthProvider] ✅ Session verified successfully')

            // Start proactive token refresh
            scheduleTokenRefresh()
          } catch (error: unknown) {
            // If we get here, the request failed even after ApiService tried to refresh
            // This means the session is truly invalid
            console.warn('[AuthProvider] ⚠️ Session verification failed:', error)
            // AuthService.handleAuthFailure is already called by ApiService on final failure
            // So we don't need to call it again here
          }
        } else {
          console.debug('[AuthProvider] No authenticated user found in storage')
        }
      } catch (error) {
        console.error('[AuthProvider] ❌ Failed to initialize auth:', error)
      }
    }

    initializeAuth()

    // Cleanup function - only clears timer, doesn't re-run effect
    return () => {
      console.debug('[AuthProvider] Cleanup: clearing refresh timer')
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }
  }, [scheduleTokenRefresh]) // scheduleTokenRefresh is stable due to useCallback

  // Listen for auth state changes to start/stop refresh timer
  // This handles login/logout that happens after initial load
  useEffect(() => {
    console.debug('[AuthProvider] Setting up auth state change listener')

    const unsubscribe = useAuthStore.subscribe((state, prevState) => {
      // Only schedule refresh if this is a change from unauthenticated to authenticated
      if (state.isAuthenticated && !prevState.isAuthenticated) {
        console.debug('[AuthProvider] User logged in, scheduling token refresh')
        scheduleTokenRefresh()
      } else if (!state.isAuthenticated && prevState.isAuthenticated) {
        // Clear timer when user logs out
        console.debug('[AuthProvider] User logged out, clearing token refresh timer')
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current)
          refreshTimerRef.current = null
        }
      }
    })

    return () => {
      console.debug('[AuthProvider] Unsubscribing from auth state changes')
      unsubscribe()
    }
  }, [scheduleTokenRefresh])

  return <>{children}</>
}
